/**
 * Quem manda parar as joias (gate V28C.2).
 *
 * ## Um observador, um ouvinte, zero RAF
 *
 * Quinze cartas não podem ter quinze observadores, quinze `requestAnimationFrame`
 * ou quinze `ResizeObserver` - foi exatamente por isso que o componente que
 * inspirou este efeito não pôde ser usado como está. Aqui há UM
 * `IntersectionObserver` para todas as cartas montadas, UM ouvinte de
 * visibilidade da página, e nenhum laço de animação: quem anima é o CSS e o
 * SMIL do filtro partilhado.
 *
 * ## Três razões para parar, e todas terminam no mesmo sítio
 *
 * 1. A carta saiu do ecrã - o observador desliga só aquela.
 * 2. A aba ficou escondida - o documento inteiro para, e as animações do SVG
 *    param com `pauseAnimations()`, que é a única forma de suster SMIL.
 * 3. O jogador pediu menos movimento - para tudo, e fica um estado elétrico
 *    parado em vez de nada.
 *
 * Fechar o inventário desmonta as cartas, o que cai no caminho 1 e, quando a
 * última sai, o observador desliga-se e o ouvinte é removido. Não fica resíduo
 * nenhum a receber eventos sobre elementos que já não existem.
 */

/** O atributo que o CSS lê para suspender tudo de uma vez. */
export const PAUSED_ATTRIBUTE = 'data-jewels-paused';

const OBSERVED = new Map<Element, (visible: boolean) => void>();
let sharedObserver: IntersectionObserver | null = null;

/** Os SVG de filtros montados. Normalmente um; a lista evita um caso especial. */
const FILTER_ROOTS = new Set<SVGSVGElement>();
let visibilityBound = false;

function prefersReducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Está tudo suspenso agora? Aba escondida ou movimento reduzido. */
function shouldPause(): boolean {
  const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
  return hidden || prefersReducedMotion();
}

/**
 * Aplica o estado a toda a gente.
 *
 * O atributo vai na raiz do documento - uma escrita, e o CSS suspende as
 * animações de todas as cartas. As do SVG não obedecem a CSS nenhum, e por isso
 * são suspensas pela API do próprio SVG.
 */
function apply(): void {
  if (typeof document === 'undefined') {
    return;
  }
  const paused = shouldPause();
  if (paused) {
    document.documentElement.setAttribute(PAUSED_ATTRIBUTE, 'yes');
  } else {
    document.documentElement.removeAttribute(PAUSED_ATTRIBUTE);
  }
  for (const svg of FILTER_ROOTS) {
    /* `pauseAnimations` não existe em jsdom; o efeito é visual, e a ausência
       dele num ambiente sem renderizador não é uma falha. */
    if (paused) {
      svg.pauseAnimations?.();
    } else {
      svg.unpauseAnimations?.();
    }
  }
}

function bindVisibility(): void {
  if (visibilityBound || typeof document === 'undefined') {
    return;
  }
  document.addEventListener('visibilitychange', apply);
  visibilityBound = true;
}

function unbindVisibility(): void {
  if (!visibilityBound || typeof document === 'undefined') {
    return;
  }
  document.removeEventListener('visibilitychange', apply);
  visibilityBound = false;
  document.documentElement.removeAttribute(PAUSED_ATTRIBUTE);
}

/**
 * Segue uma carta, e diz quando ela entra e sai do ecrã.
 *
 * Devolve a função que a deixa de seguir. Quando a última sai, o observador é
 * desligado e o ouvinte de visibilidade é removido: fechar o inventário não
 * pode deixar nada a correr.
 */
export function observeJewelCard(
  element: Element,
  onChange: (visible: boolean) => void,
): () => void {
  bindVisibility();
  apply();
  if (typeof IntersectionObserver === 'undefined') {
    /* Ambiente sem observador - jsdom, por exemplo. Anima, e não parte. */
    onChange(true);
    return () => {
      if (OBSERVED.size === 0) {
        unbindVisibility();
      }
    };
  }
  if (!sharedObserver) {
    sharedObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        OBSERVED.get(entry.target)?.(entry.isIntersecting);
      }
    });
  }
  OBSERVED.set(element, onChange);
  sharedObserver.observe(element);
  return () => {
    OBSERVED.delete(element);
    sharedObserver?.unobserve(element);
    if (OBSERVED.size === 0) {
      sharedObserver?.disconnect();
      sharedObserver = null;
      unbindVisibility();
    }
  };
}

/** Regista o SVG dos filtros, para o poder suspender. */
export function registerJewelFilters(svg: SVGSVGElement): () => void {
  FILTER_ROOTS.add(svg);
  bindVisibility();
  apply();
  return () => {
    FILTER_ROOTS.delete(svg);
    if (OBSERVED.size === 0 && FILTER_ROOTS.size === 0) {
      unbindVisibility();
    }
  };
}

/** Só para as provas: quantos observadores e ouvintes estão vivos. */
export function jewelMotionState() {
  return {
    observers: sharedObserver ? 1 : 0,
    observed: OBSERVED.size,
    filterRoots: FILTER_ROOTS.size,
    visibilityBound,
  };
}
